import { IUserAvatar } from '@interfaces/avatar';
import { IUser } from '@interfaces/user';

/**
 * `avatarId` the backend assigns to the gender-based default avatar served in
 * `/api/v1/profile` as `defaultAvatar`. It is not a real avatar record —
 * nothing on the backend resolves it — so every place that treats an id as a
 * pointer into `user.avatars` has to special-case it.
 */
export const DEFAULT_AVATAR_ID = 'default-avatar';

/**
 * Tells whether the avatar is the backend-provided default one rather than an
 * avatar the user created.
 *
 * @param avatar - Avatar to test.
 * @returns True for the default avatar.
 */
export const isDefaultAvatar = (avatar?: IUserAvatar | null): boolean =>
  avatar?.avatarId === DEFAULT_AVATAR_ID;

/**
 * Tells whether the avatar selection step has anything to offer: avatars the
 * user created, or the default one. The default is only served when the
 * participant has a gender set, so it can legitimately be missing and leave
 * the step with nothing to select.
 *
 * @param user - Profile holding `avatars` and `defaultAvatar`.
 * @returns True when the step can render at least one selectable avatar.
 */
export const hasSelectableAvatars = (user?: IUser | null): boolean =>
  !!user?.avatars?.length || !!user?.defaultAvatar?.avatarUrl;

/**
 * Builds the selection list for the avatar step: the user's own avatars plus
 * the default one, appended last so a freshly created avatar keeps its place.
 *
 * @param avatars - Avatars the user created.
 * @param defaultAvatar - Default avatar from the profile; ignored without a model url.
 * @returns List to render on the avatar step.
 */
export const withDefaultAvatar = (
  avatars: IUserAvatar[],
  defaultAvatar?: IUserAvatar | null,
): IUserAvatar[] => {
  if (!defaultAvatar?.avatarUrl) {
    return avatars;
  }

  return [
    ...avatars.filter((avatar) => !isDefaultAvatar(avatar)),
    defaultAvatar,
  ];
};

/**
 * Picks the avatar the selection step opens on, so entering an experience stays
 * the one click the step is meant to be instead of forcing a pick before Next
 * unlocks. An avatar of the user's own wins over the default one, which must
 * never quietly replace it; with nothing of their own — a first-time user, or
 * an active id one of the reset flows cleared — the default is preselected.
 *
 * @param avatars - Selection list of the step, default avatar included.
 * @param activeAvatarId - Avatar the profile currently marks as active.
 * @returns Avatar to preselect, or null when the list is empty.
 */
export const resolvePreselectedAvatar = (
  avatars: IUserAvatar[],
  activeAvatarId?: string,
): IUserAvatar | null => {
  const activeAvatar =
    activeAvatarId &&
    avatars.find((avatar) => avatar.avatarId === activeAvatarId);

  if (activeAvatar) {
    return activeAvatar;
  }

  const ownAvatar = avatars.find((avatar) => !isDefaultAvatar(avatar));

  return ownAvatar || avatars.find(isDefaultAvatar) || null;
};
